import React from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerBody,
  DrawerHeader,
  CloseButton,
  HStack,
  Heading,
  VStack,
  Checkbox,
  Button,
  Text,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
  Icon
} from '@chakra-ui/react';
import { ColumnDef, VisibilityState, ColumnOrderState } from '@tanstack/react-table';
import { FiSearch } from 'react-icons/fi';
import { MdDragIndicator } from 'react-icons/md';
import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';

export type ColumnVisibilitySidebarProps<Data extends object> = {
  isOpen: boolean;
  onClose: () => void;
  columns: ColumnDef<Data, any>[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => void;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: (updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState)) => void;
};

interface SortableColumnItemProps {
  id: string;
  header: string;
  isVisible: boolean;
  onToggleVisibility: () => void;
}

function SortableColumnItem({ id, header, isVisible, onToggleVisibility }: SortableColumnItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      display="flex"
      alignItems="center"
      p={1}
      borderRadius="md"
      bg={isDragging ? 'gray.100' : 'transparent'}
      _hover={{ bg: 'gray.50' }}
      cursor="grab"
      _active={{ cursor: 'grabbing' }}
      {...attributes}
      {...listeners}
    >
      <Box
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          isChecked={isVisible}
          onChange={onToggleVisibility}
          colorScheme="blue"
        >
          {header}
        </Checkbox>
      </Box>
      <Box flex={1} />
      <Icon
        as={MdDragIndicator}
        color="gray.400"
        ml={2}
      />
    </Box>
  );
}

export function ColumnVisibilitySidebar<Data extends object>({
  isOpen,
  onClose,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange
}: ColumnVisibilitySidebarProps<Data>) {
  
  const [searchQuery, setSearchQuery] = useState('');
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  const visibleColumnCount = Object.values(columnVisibility).filter(Boolean).length;
  const totalColumns = columns.length;
  const isAllVisible = visibleColumnCount === totalColumns;
  const isNoneVisible = visibleColumnCount === 0;

  // Get ordered columns based on columnOrder or default order
  const orderedColumns = React.useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) {
      return columns;
    }
    
    // Create a map for quick lookup
    const columnMap = new Map(columns.map(col => [col.id, col]));
    
    // Order columns according to columnOrder, with any missing columns at the end
    const ordered = columnOrder
      .map(id => columnMap.get(id))
      .filter(Boolean) as ColumnDef<Data, any>[];
    
    // Add any columns not in the order
    const orderedIds = new Set(columnOrder);
    const remaining = columns.filter(col => col.id && !orderedIds.has(col.id));
    
    return [...ordered, ...remaining];
  }, [columns, columnOrder]);

  const filteredColumns = orderedColumns.filter(column => {
    const columnId = column.id;
    if (!columnId) return false;
    
    const header = typeof column.header === 'string' 
      ? column.header 
      : columnId;
    
    return header.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleToggleColumn = (columnId: string) => {
    onColumnVisibilityChange(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  const handleShowAll = () => {
    const allVisible: VisibilityState = {};
    columns.forEach(column => {
      if (column.id) {
        allVisible[column.id] = true;
      }
    });
    onColumnVisibilityChange(allVisible);
  };

  const handleHideAll = () => {
    const allHidden: VisibilityState = {};
    columns.forEach(column => {
      if (column.id) {
        allHidden[column.id] = false;
      }
    });
    onColumnVisibilityChange(allHidden);
  };

  const handleReset = () => {
    const defaultVisibility: VisibilityState = {};
    columns.forEach(column => {
      if (column.id) {
        defaultVisibility[column.id] = (column.meta as any)?.defaultVisible ?? true;
      }
    });
    onColumnVisibilityChange(defaultVisibility);
    
    // Reset column order to default
    if (onColumnOrderChange) {
      onColumnOrderChange(columns.map(col => col.id as string));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && onColumnOrderChange) {
      const oldIndex = orderedColumns.findIndex(col => col.id === active.id);
      const newIndex = orderedColumns.findIndex(col => col.id === over?.id);
      
      const newOrder = arrayMove(orderedColumns.map(col => col.id as string), oldIndex, newIndex);
      onColumnOrderChange(newOrder);
    }
  };

  return (
    <Drawer
      placement='right'
      onClose={onClose}
      isOpen={isOpen}
      size="sm"
      blockScrollOnMount={false}
      trapFocus={false}
    >
      <DrawerContent className="no-print" marginTop={120} p='0'>
        <DrawerHeader px='4!important' borderBottomWidth='1px'>
          <HStack justify='space-between'>
            <Heading size='sm'>Columns</Heading>
            <CloseButton onClick={onClose} />
          </HStack>
          <Text fontSize='sm' color='fg.muted' mt='2'>
            {visibleColumnCount} of {totalColumns} columns visible
          </Text>
        </DrawerHeader>
        
        <DrawerBody px='4!important' py='4'>
          <VStack align='stretch' spacing='4'>
            <HStack spacing='2'>
              <Button 
                size='sm' 
                variant='outline' 
                onClick={handleShowAll}
                isDisabled={isAllVisible}
              >
                Show All
              </Button>
              <Button 
                size='sm' 
                variant='outline' 
                onClick={handleHideAll}
                isDisabled={isNoneVisible}
              >
                Hide All
              </Button>
              <Button size='sm' variant='outline' onClick={handleReset}>
                Reset
              </Button>
            </HStack>
            
            <Text fontSize='sm' color='fg.muted'>
              Drag columns to reorder them. Click checkboxes to show/hide columns.
            </Text>
            
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
            
            <Divider />
            
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={filteredColumns.map(col => col.id as string)}
                strategy={verticalListSortingStrategy}
              >
                <VStack align='stretch' spacing='1'>
                  {filteredColumns.map((column) => {
                    const columnId = column.id;
                    if (!columnId) return null;
                    
                    const isVisible = columnVisibility[columnId] !== false;
                    const header = typeof column.header === 'string' 
                      ? column.header 
                      : columnId;
                    
                    return (
                      <SortableColumnItem
                        key={columnId}
                        id={columnId}
                        header={header}
                        isVisible={isVisible}
                        onToggleVisibility={() => handleToggleColumn(columnId)}
                      />
                    );
                  })}
                </VStack>
              </SortableContext>
            </DndContext>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
